import type { ComponentChildren } from 'preact';
import { COUNTRIES } from '../../utils/countries.js';
import {
  type IdentityNumberValue,
  birthDateText,
  formatMykad,
} from '../../utils/identityNumber.js';
import { FIELD_LABEL_CLASS } from '../ui/fieldLabel.js';

/**
 * 366 (#1223, design 1c): the customer's identity number — a MyKad / Passport switch over one field. MyKad gets its
 * dashes while typing and a "Born <date>" read-back, so a mistyped digit shows before saving; Passport asks the issuing
 * country and the number. Used by the sign-up form and the required screen.
 */
export function IdentityNumberField({
  value,
  onChange,
  error,
  errorAction,
  label = 'Identity Number',
}: {
  value: IdentityNumberValue;
  onChange: (value: IdentityNumberValue) => void;
  error?: string;
  /** Shown after the error sentence (the duplicate's "Contact us"). */
  errorAction?: ComponentChildren;
  label?: string | null;
}) {
  const born = value.kind === 'mykad' ? birthDateText(value.number) : null;
  const invalid = error
    ? { 'aria-invalid': 'true' as const, 'aria-describedby': 'identity-number-error' }
    : {};

  function choose(kind: IdentityNumberValue['kind']) {
    if (kind === value.kind) return;
    onChange(kind === 'mykad' ? { kind, number: '' } : { kind, country: '', number: '' });
  }

  const segment = (kind: IdentityNumberValue['kind'], text: string) => (
    <label
      className={[
        'flex min-h-11 cursor-pointer items-center justify-center rounded-full text-sm font-semibold',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary',
        value.kind === kind ? 'bg-primary text-on-primary' : 'text-text-muted',
      ].join(' ')}
    >
      <input
        type="radio"
        name="identity_kind"
        value={kind}
        checked={value.kind === kind}
        onChange={() => choose(kind)}
        className="sr-only"
      />
      {text}
    </label>
  );

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor="identity_number" className={FIELD_LABEL_CLASS}>
          {label}
        </label>
      )}
      <fieldset
        aria-label="Identity document"
        className="grid grid-cols-2 gap-1 rounded-full bg-surface-mid p-1"
      >
        {segment('mykad', 'MyKad')}
        {segment('passport', 'Passport')}
      </fieldset>

      {value.kind === 'mykad' ? (
        <input
          id="identity_number"
          name="identity_number"
          data-testid="identity-number"
          inputMode="numeric"
          autoComplete="off"
          placeholder="e.g. 900101-01-1234"
          value={value.number}
          onInput={(e) =>
            onChange({ kind: 'mykad', number: formatMykad((e.target as HTMLInputElement).value) })
          }
          {...invalid}
          className="bg-surface-mid text-text rounded-lg px-4 py-3 font-mono tracking-wide"
        />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <select
            aria-label="Passport country"
            data-testid="identity-country"
            value={value.country ?? ''}
            onChange={(e) => onChange({ ...value, country: (e.target as HTMLSelectElement).value })}
            className="bg-surface-mid text-text rounded-lg px-3 py-3"
          >
            <option value="">Country</option>
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
          <input
            id="identity_number"
            name="identity_number"
            data-testid="identity-number"
            autoComplete="off"
            aria-label="Passport number"
            placeholder="Passport no."
            value={value.number}
            onInput={(e) => onChange({ ...value, number: (e.target as HTMLInputElement).value })}
            {...invalid}
            className="bg-surface-mid text-text rounded-lg px-4 py-3 font-mono tracking-wide uppercase"
          />
        </div>
      )}

      {born && !error && (
        <span
          data-testid="identity-birth"
          className="inline-flex w-fit items-center gap-1.5 rounded-full bg-surface-card px-3 py-1 text-xs text-text-muted"
        >
          {born}
        </span>
      )}
      {error ? (
        <span id="identity-number-error" role="alert" className="text-sm text-error">
          {error} {errorAction}
        </span>
      ) : (
        <span className="text-xs text-text-muted">
          {value.kind === 'mykad'
            ? 'The 12 digits on your MyKad. One account per person.'
            : 'The issuing country and the number on your passport.'}
        </span>
      )}
    </div>
  );
}
